import { createContext, useContext, useCallback, useEffect, useState } from 'react'
import { defaultPreferences, Preferences, PreferencesConfig, readPreferences, writePreferences } from '../../utils/Preferences'
import { gtagConsentUpdate } from '../../utils/Consent'

const PreferencesContext = createContext<{
  isDialogOpen: boolean
  isBannerHidden: boolean
  preferences?: Preferences
  saveConfig: (config: PreferencesConfig) => void
  acceptAll: () => void
  acceptNecessary: () => void
  setDialogOpen: (value: boolean) => void
} | null>(null)

export function usePreferences() {
  const ctx = useContext(PreferencesContext)
  if (!ctx) throw Error('Cannot call usePreferences outside of PreferencesProvider')
  return ctx
}

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  // an initial value is set to hide banner while reading cookies
  const [preferences, setPreferences] = useState<Preferences | undefined>(defaultPreferences)
  const [isDialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    const stored = readPreferences()
    setPreferences(stored)
    if (stored) gtagConsentUpdate(stored)
  }, [])

  const saveConfig = useCallback((config: PreferencesConfig) => {
    const next = {
      ...config,
      necessary: true,
    } as const
    writePreferences(next)
    gtagConsentUpdate(next)
    setPreferences(next)
    setDialogOpen(false)
  }, [])

  const acceptAll = useCallback(() => {
    saveConfig({ analytics: true, marketing: true })
  }, [saveConfig])

  const acceptNecessary = useCallback(() => {
    saveConfig({ analytics: false, marketing: false })
  }, [saveConfig])

  const isBannerDisplayed = isDialogOpen || !!preferences

  return (
    <PreferencesContext.Provider
      value={{
        isDialogOpen,
        isBannerHidden: isBannerDisplayed,
        preferences,
        saveConfig,
        acceptAll,
        acceptNecessary,
        setDialogOpen,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  )
}
